/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Suspense, useEffect} from "react";
import {Route, Routes} from "react-router-dom";
import {cameraSlice, radarSlice, ultrasonicSlice, vehicleProgramSlice, lidarSlice} from "../redux/ducks/Wizard/slices";
import {ReactRouter6Adapter} from "use-query-params/adapters/react-router-6";
import queryString from "query-string";
import {QueryParamProvider} from "use-query-params";
import {MsalAuthenticationTemplate} from '@azure/msal-react';
import {loginRequest} from '../auth/authConfig';
import {InteractionType} from '@azure/msal-browser';
import LoadingBar from 'react-top-loading-bar'
import {lazyWithPreload} from "react-lazy-with-preload";

// Argos components
import NotFound from "../containers/pages/navigation/NotFound";
import Home from "../containers/pages/navigation/Home";
import RequestAccess from "../containers/pages/admin/RequestAccess";
import LandingPage from "../containers/pages/navigation/Landing/Landing";
import USERS from "../containers/pages/admin/AddUser";
import SliceProvider from "../redux/ducks/Wizard/SliceProvider";
import {
  coverageRequirementsDetails,
  coverageRequirementsLandingRoute
} from "../containers/pages/CoverageRequirements/constants";
import ADAS_PROGRAMS_CONSTANTS from "../containers/pages/AdasPrograms/constants";
import AdasPrograms from "../containers/pages/AdasPrograms/AdasPrograms";
import {SCENARIO_ANALYSIS_CONSTANTS} from "../containers/pages/ScenarioAnalysis/constants";
import {VEHICLE_PROGRAMS_CONSTANTS} from "../containers/pages/vehiclePrograms/constants";
import Link from "./Route";
import ReportViewWrapper from "../containers/pages/vehiclePrograms/ItemDetails/Tabs/SensingSysPerfTab/ReportViewWrapper";
import SensorLidar from "../containers/pages/lidar/SensorLidar";
import SensorUltrasonic from "../containers/pages/ultrasonic/SensorUltrasonic";
import { ReportView } from "../containers/components/Reports/ReportView";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from "../containers/pages/SensingSystemArchitectureDesign/constants";


const SensorRadar = lazyWithPreload(() => import("../containers/pages/radar/SensorRadar/SensorRadar"));
const SensorCamera = lazyWithPreload(() => import("../containers/pages/camera/SensorCamera/SensorCamera"));
const CoverageRequirements = lazyWithPreload(() => import("../containers/pages/CoverageRequirements/CoverageRequirements"));

const VehicleProgram = lazyWithPreload(() => import("../containers/pages/vehiclePrograms/VehicleProgram/VehicleProgram"));
const VehicleProgramItemDetails = lazyWithPreload(() => import("../containers/pages/vehiclePrograms/ItemDetails/VehicleProgramItemDetails"));

const ObjectDetails = lazyWithPreload(() => import("../containers/pages/objectApp/ItemDetails/ObjectDetails"));
const PotentiallyMovingObjectDetails = lazyWithPreload(() => import("../containers/pages/realWorldObjects/PotentiallyMovingObjects/PotentiallyMovingObjectsDetails"));
const MiscellaneousObjectsDetails = lazyWithPreload(() => import("../containers/pages/realWorldObjects/Miscellaneous/MiscellaneousObjectsDetails"));
const SignsRWODetails = lazyWithPreload(() => import("../containers/pages/realWorldObjects/Signs/SignsRWODetails"));

const CameraItemDetails = lazyWithPreload(() => import("../containers/pages/camera/SensorCamera/ItemDetails/CameraItemDetails"));
const SensorItemDetails = lazyWithPreload(() => import("../containers/pages/radar/SensorRadar/ItemDetails/RadarItemDetails"));
const SensorUltrasonicItemDetails = lazyWithPreload(() => import("../containers/pages/ultrasonic/ItemDetails/UltrasonicItemDetails"))
const SensorLidarItemDetails = lazyWithPreload(() => import("../containers/pages/lidar/ItemDetails/LidarItemDetails"))
const Objects = lazyWithPreload(() => import("../containers/pages/objectApp/Objects"));
const Admin = lazyWithPreload(() => import("../containers/pages/admin/AdminAccesscontrol"));
const AdasProgramsItemDetails = lazyWithPreload(() => import("../containers/pages/AdasPrograms/ItemDetails/AdasProgramsItemDetails"));
const CoverageRequirementsStudyDetails = lazyWithPreload(() => import("../containers/pages/CoverageRequirements/ItemDetails/CoverageRequirementsStudyDetails"));

/* Scenario Analysis */
const ScenarioAnalysis = lazyWithPreload(() => import("../containers/pages/ScenarioAnalysis/Landing/ScenarioAnalysis"));
const ViewModel = lazyWithPreload(() => import("../containers/pages/ScenarioAnalysis/ViewModel/ViewModel"));
const ViewVariables = lazyWithPreload(() => import("../containers/pages/ScenarioAnalysis/ViewVariables/ViewVariables"));

/* Real World Objects */ 
const PotentiallyMovingObjects = lazyWithPreload(() => import("../containers/pages/realWorldObjects/PotentiallyMovingObjects/PotentiallyMovingObjects"))
const ObjectClassification = lazyWithPreload(() => import("../containers/pages/realWorldObjects/ObjectClassification"));
const SignsRWO = lazyWithPreload(() => import("../containers/pages/realWorldObjects/Signs/SignsRWO"))
const SignsPicklist = lazyWithPreload(() => import("../containers/pages/realWorldObjects/Signs/SignsPicklist"))
const MiscellaneousObjects = lazyWithPreload(() => import("../containers/pages/realWorldObjects/Miscellaneous/MiscellaneousObjects"))

/** Sensing System Architecture Design */
const SensingSystemArchitectureDesignLanding = lazyWithPreload(() => import("../containers/pages/SensingSystemArchitectureDesign/Landing/LandingPage"));
const SensingSystemArchitectureDesignViewStudy = lazyWithPreload(() => import("../containers/pages/SensingSystemArchitectureDesign/ViewStudy/ViewStudyPage"));


const ErrorComponent = (error: any) => {
  console.log(error)
  // return <p>An Error Occurred: {error}</p>;
  return <p>An Msal Error Occurred: </p>;
}

const LoadingComponent = () => {
  return <LoadingBar
    color='#8800ff'
    progress={100}
  />
}
/**
 * Returns the average of two numbers.
 *
 * @remarks
 * This method is part of the {@link core-library#Statistics | Statistics subsystem}.
 *
 * @param x - The first input number
 * @param y - The second input number
 * @returns The arithmetic mean of `x` and `y`
 *
 * @beta
 */
export default function Links() {

  useEffect(() => {
    /**
     * preload only the sidebar routes
     */
    SensorRadar.preload()
    SensorCamera.preload()
    CoverageRequirements.preload()
    VehicleProgram.preload()
    ScenarioAnalysis.preload()
    SensingSystemArchitectureDesignLanding.preload()
    SensingSystemArchitectureDesignViewStudy.preload()
    Objects.preload()
    Admin.preload()
    CoverageRequirementsStudyDetails.preload()
  }, [])

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        searchStringToObject: queryString.parse,
        objectToSearchString: queryString.stringify,
      }}
    >
      <Suspense>
        <Routes>
          {/* eslint-disable-next-line react/jsx-no-undef */}
          <Route path="/" element={<Home/>}/>
          {
            /* Finally, catch all unmatched routes */
          }
          <Route path="/home" element={<Home/>}/>
          <Route path="/landing" element={<CustomRoute element={<LandingPage/>}/>}/>

          <Route path="/Admin" element={<CustomRoute element={<Admin/>}/>}/>

          <Route path="/addUser" element={<CustomRoute element={<USERS/>}/>}/>

          <Route path="/requestaccess" element={<CustomRoute element={<RequestAccess/>}/>}/>
          <Route path="/objects" element={<CustomRoute element={<Objects/>}/>}/>

          <Route path="/potentiallymovingobjects" element={<CustomRoute element={<PotentiallyMovingObjects/>}/>}/>
          <Route path="/objectclassification" element={<CustomRoute element={<ObjectClassification/>}/>}/>
          <Route path="/signs" element={<CustomRoute element={<SignsRWO/>}/>}/>
          <Route path="/signspicklist" element={<CustomRoute element={<SignsPicklist/>}/>}/>
          <Route path="/miscellaneousObjects" element={<CustomRoute element={<MiscellaneousObjects/>}/>}/>

          <Route path="/sensorradar" element={<CustomRoute element={<SliceProvider slice={radarSlice}>
            <SensorRadar/>
          </SliceProvider>}/>}/>

          <Route path="/sensorcamera" element={<CustomRoute element={<SliceProvider slice={cameraSlice}>
            <SensorCamera/>
          </SliceProvider>}/>}/>

          <Route path="/sensorlidar" element={<CustomRoute element={<SliceProvider slice={lidarSlice}>
            <SensorLidar/>
          </SliceProvider>}/>}/>

          <Route path="/sensorultrasonic" element={<CustomRoute element={<SliceProvider slice={ultrasonicSlice}>
            <SensorUltrasonic/>
          </SliceProvider>}/>}/>

          {/* Objects Details */}
          <Route path="/objectDetails" element={<CustomRoute element={<ObjectDetails/>}/>}/>
          <Route path="/signsrwodetails" element={<CustomRoute element={<SignsRWODetails/>}/>}/>
          <Route path="/potentiallymovingobjectdetails" element={<CustomRoute element={<PotentiallyMovingObjectDetails/>}/>}/>
          <Route path="/miscellaneousobjectsdetails" element={<CustomRoute element={<MiscellaneousObjectsDetails/>}/>}/>

          <Route path="/radarPerformance" element={<CustomRoute element={<SensorItemDetails/>}/>}/>
          <Route path="/cameraperformance" element={<CustomRoute element={<CameraItemDetails/>}/>}/>
          <Route path="/lidarPerformance" element={<CustomRoute element={<SensorLidarItemDetails/>}/>}/>
          <Route path="/ultrasonicPerformance" element={<CustomRoute element={<SensorUltrasonicItemDetails/>}/>}/>

          <Route path='/vehicleprogram' element={<CustomRoute element={<SliceProvider slice={vehicleProgramSlice}>
            <VehicleProgram/>
          </SliceProvider>}/>}
          />


          <Route path={VEHICLE_PROGRAMS_CONSTANTS.itemDetails.route}
                 element={<CustomRoute element={<VehicleProgramItemDetails/>}/>}/>
          <Route path={VEHICLE_PROGRAMS_CONSTANTS.itemDetails.sensingSystemPerformance.viewReportRoute}
                 element={<CustomRoute element={<ReportViewWrapper/>}/>}/>

          {/* Scenario Analysis */}
          <Route path={SCENARIO_ANALYSIS_CONSTANTS.landing.route}
                 element={<CustomRoute element={<ScenarioAnalysis/>}/>}/>
          <Route path={SCENARIO_ANALYSIS_CONSTANTS.viewModel.route}
                 element={<CustomRoute element={<ViewModel/>}/>}/>
          <Route path={SCENARIO_ANALYSIS_CONSTANTS.viewVariables.route}
                 element={<CustomRoute element={<ViewVariables/>}/>}/>

          {/* Sensing System Architecture Design */}
          <Route path={SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.route}
                 element={<CustomRoute element={<SensingSystemArchitectureDesignLanding />} />} />
          <Route path={SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.route}
                 element={<CustomRoute element={<SensingSystemArchitectureDesignViewStudy />} />} />

          <Route path={coverageRequirementsLandingRoute} element={<CustomRoute element={<CoverageRequirements/>}/>}/>

          <Route path={ADAS_PROGRAMS_CONSTANTS.mainRoute} element={<CustomRoute element={<AdasPrograms/>}/>}/>

          <Route path={ADAS_PROGRAMS_CONSTANTS.itemDetailsRoute}
                 element={<CustomRoute element={<AdasProgramsItemDetails/>}/>}/>
          <Route path={ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.viewReportRoute}
                 element={<CustomRoute element={<ReportView/>}/>}/>

          <Route path={coverageRequirementsDetails}
                 element={<CustomRoute element={<CoverageRequirementsStudyDetails/>}/>}/>

          <Route path="*" element={<CustomRoute element={<NotFound/>}/>}/>;

        </Routes>
      </Suspense>
    </QueryParamProvider>
  );
}

const CustomRoute = (props: any) => {
  const {disableMsal, path, element, sliceElement} = props

  return (
    <>
      {false ? <Link componentSub={
          element}/> :
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={loginRequest}
          errorComponent={ErrorComponent}
          loadingComponent={LoadingComponent}>
          <Link componentSub={
            element}/>
        </MsalAuthenticationTemplate>
      }
    </>
  )
}

